<template>
  <div class="spinner-container">
    <div
      v-if="lastClickX !== null && lastClickY !== null && showAnimation"
      class="placeholder p-0.5"
      :class="{
        'w-4 h-2': size === 'sm',
        'w-6 h-2': size === 'base',
        'w-8 h-2': size === 'lg',
      }"
    ></div>
    <svg
      :key="showAnimation"
      id="spinner"
      aria-hidden="true"
      class="inline text-header fill-accent p-0.5"
      :class="{
        'w-4 h-4': size === 'sm',
        'w-6 h-6': size === 'base',
        'w-8 h-8': size === 'lg',
        'animate-spin':
          (progress === null || progress === 0) &&
          (lastClickX === null || targetX === null),
        'translate-and-spin':
          showAnimation &&
          lastClickX !== null &&
          targetX !== null &&
          (progress === null || progress === 0),
      }"
      :style="translationStyle"
      viewBox="0 0 100 101"
      fill="none"
    >
      <path
        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
        fill="currentColor"
      />

      <path
        v-if="progress !== null && progress > 0"
        :d="progressPath"
        fill="currentFill"
        transform="rotate(-90 50 50.5)"
      />
      <path
        v-else
        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
        fill="currentFill"
      />
    </svg>
  </div>
</template>

<script>
export default {
  name: 'HitSpinner',
  props: {
    size: {
      type: String,
      required: false,
      default: 'base',
      validator: function (value) {
        return ['sm', 'base', 'lg'].indexOf(value) !== -1;
      },
    },
    progress: {
      type: Number,
      required: false,
      default: null,
    },
    lastClickX: {
      type: Number,
      required: false,
      default: null,
    },
    lastClickY: {
      type: Number,
      required: false,
      default: null,
    },
    targetX: {
      type: Number,
      required: false,
      default: null,
    },
    targetY: {
      type: Number,
      required: false,
      default: null,
    },
    showAnimation: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  computed: {
    progressPath() {
      if (this.progress === null) {
        return 'M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z';
      }

      const progress = Math.min(Math.max(this.progress, 0), 100);
      const angle = (progress / 100) * 360;

      const rad = angle * (Math.PI / 180);
      const x = 50 + 40.9186 * Math.cos(rad);
      const y = 50.5 + 40.9186 * Math.sin(rad);
      const x2 = 50 + 49.9186 * Math.cos(rad);
      const y2 = 50.5 + 49.9186 * Math.sin(rad);

      const largeArcFlag = angle <= 180 ? '0' : '1';

      return `M 90.9186 50.5 A 40.9186 40.9186 0 ${largeArcFlag} 1 ${x} ${y} L ${x2} ${y2} A 49.9186 49.9186 0 ${largeArcFlag} 0 100 50.5 Z`;
    },
    translationStyle() {
      if (this.lastClickX === null || this.lastClickY === null) {
        return {};
      }
      return {
        '--start-x': `${this.lastClickX}px`,
        '--start-y': `${this.lastClickY}px`,
        '--target-x': `${this.targetX}px`,
        '--target-y': `${this.targetY}px`,
      };
    },
  },
};
</script>

<style scoped>
.spinner-container {
  position: relative;
  display: inline-block;
}

.placeholder {
  display: inline-block;
  visibility: hidden;
}

@keyframes translate-to-position {
  0% {
    position: fixed;
    left: var(--start-x);
    top: var(--start-y);
    transform: translate(-50%, -50%);
    pointer-events: none;
  }
  100% {
    position: fixed;
    left: var(--start-x);
    top: var(--start-y);
    transform: translate(-50%, -50%)
      translate(
        calc(var(--target-x) - var(--start-x)),
        calc(var(--target-y) - var(--start-y))
      );
    pointer-events: none;
  }
}

@keyframes spin {
  to {
    transform: translate(-50%, -50%)
      translate(
        calc(var(--target-x) - var(--start-x)),
        calc(var(--target-y) - var(--start-y))
      )
      rotate(360deg);
  }
}

.translate-and-spin {
  animation: translate-to-position 0.8s ease-out forwards,
    spin 1s linear infinite 0.5s;
}
</style>
